import { FlowContext } from 'src/lib/flow';
import { newFlowPage } from 'src/lib/flow/FlowPage';
import { ImageSelectQuestion } from 'src/lib/flow/FlowQuestion';
import { isAgent } from 'src/lib/flow/pageHelpers';
import { your, yourHome } from 'src/lib/flow/text';

type ConditionOptions = {
  fixer_upper: { img: string; desc: string };
  dated: { img: string; desc: string };
  standard: { img: string; desc: string };
  high_end: { img: string; desc: string };
  luxury: { img: string; desc: string };
};

const labelsByKey = {
  fixer_upper: 'Fixer Upper',
  dated: 'Dated',
  standard: 'Standard',
  high_end: 'High End',
  luxury: 'Luxury',
};

const getOptions = (opts: ConditionOptions) => {
  return Object.keys(opts).map((key) => ({
    label: labelsByKey[key],
    value: key,
    description: opts[key].desc,
    imageUrl: opts[key].img,
  }));
};

const getImageSelect = (key: string, options: ConditionOptions): ImageSelectQuestion => ({
  key,
  type: 'image_single_select',
  options: getOptions(options),
});

const imgix = (path: string) => `https://imgdrop.imgix.net/${path}`;

const isEnabled = ({ answers, inputContext: { channel } }: FlowContext) => {
  return !isAgent(answers, channel);
};

export const kitchenCondition = newFlowPage({
  slug: 'kitchen-condition',
  title: ({ answers, inputContext: { channel } }: FlowContext) => {
    return `How would you describe ${your(answers, channel)} kitchen?`;
  },
  subtitle: () => 'For these questions, just select the closest match.',
  autoAdvance: true,
  isEnabled,
  questions: [
    getImageSelect('home.kitchen_seller_score', {
      fixer_upper: {
        desc: 'Kitchen needs significant repairs',
        img: imgix('2023-04-1681772974083-61414.png?fm=jpg&auto=format'),
      },
      dated: {
        desc: "Kitchen hasn't been updated recently",
        img: imgix('2023-04-1681772974089-39460.png?fm=jpg&auto=format'),
      },
      standard: {
        desc: 'Kitchen is updated with common finishes',
        img: imgix('2023-04-1681772974093-51260.png?fm=jpg&auto=format'),
      },
      high_end: {
        desc: 'Kitchen has high-quality upgrades',
        img: imgix('2023-04-1682444345875-57331.jpeg?auto=format&w=512'),
      },
      luxury: {
        desc: 'Kitchen has elegant, top-tier finishes',
        img: imgix('2023-04-1682444629335-69661.jpeg?auto=format&w=512'),
      },
    }),
  ],
});

export const bathroomCondition = newFlowPage({
  slug: 'bathroom-condition',
  title: ({ answers, inputContext: { channel } }: FlowContext) => {
    return `How would you describe ${your(answers, channel)} main bathroom?`;
  },
  autoAdvance: true,
  isEnabled,
  questions: [
    getImageSelect('home.bathroom_seller_score', {
      fixer_upper: {
        desc: 'Bathroom needs significant repairs',
        img: imgix('2023-04-1682103091299-33543.jpeg?auto=format&w=512'),
      },
      dated: {
        desc: "Bathroom hasn't been updated recently",
        img: imgix('2023-04-1681773104207-98555.png?fm=jpg&auto=format'),
      },
      standard: {
        desc: 'Bathroom is updated with common finishes',
        img: imgix('2023-04-1682103088251-79730.jpeg?auto=format&w=512'),
      },
      high_end: {
        desc: 'Bathroom has high-quality upgrades',
        img: imgix('2023-04-1681773104214-77488.png?fm=jpg&auto=format'),
      },
      luxury: {
        desc: 'Bathroom has elegant, top-tier finishes',
        img: imgix('2023-04-1681773104217-27390.png?fm=jpg&auto=format'),
      },
    }),
  ],
});

export const livingRoomCondition = newFlowPage({
  slug: 'living-room-condition',
  title: ({ answers, inputContext: { channel } }: FlowContext) => {
    return `How would you describe ${your(answers, channel)} living room?`;
  },
  autoAdvance: true,
  isEnabled,
  questions: [
    getImageSelect('home.living_room_seller_score', {
      fixer_upper: {
        desc: 'Living room needs significant repairs',
        img: imgix('2023-04-1681773170815-60328.png?fm=jpg&auto=format'),
      },
      dated: {
        desc: "Living room hasn't been updated recently",
        img: imgix('2023-04-1681773170823-03451.png?fm=jpg&auto=format'),
      },
      standard: {
        desc: 'Living room is updated with common finishes',
        img: imgix('2023-04-1681773170826-47471.png?fm=jpg&auto=format'),
      },
      high_end: {
        desc: 'Living room has high-quality upgrades',
        img: imgix('2023-04-1681773170828-76415.png?fm=jpg&auto=format'),
      },
      luxury: {
        desc: 'Living room has elegant, top-tier finishes',
        img: imgix('2023-04-1681773170832-20699.png?fm=jpg&auto=format'),
      },
    }),
  ],
});

export const exteriorCondition = newFlowPage({
  slug: 'exterior-condition',
  title: ({ answers, inputContext: { channel } }: FlowContext) => {
    return `How would you describe ${yourHome(answers, channel)} exterior?`;
  },
  autoAdvance: true,
  isEnabled,
  questions: [
    getImageSelect('home.exterior_seller_score', {
      fixer_upper: {
        desc: 'Exterior needs significant repairs',
        img: imgix('2023-04-1681773220493-46489.png?fm=jpg&auto=format'),
      },
      dated: {
        desc: "Exterior hasn't been updated recently",
        img: imgix('2023-04-1681773220495-22229.png?fm=jpg&auto=format'),
      },
      standard: {
        desc: 'Exterior is updated with common finishes',
        img: imgix('2023-04-1681773220498-64940.png?fm=jpg&auto=format'),
      },
      high_end: {
        desc: 'Exterior has high-quality upgrades',
        img: imgix('2023-04-1682103085417-24272.jpeg?auto=format&w=512'),
      },
      luxury: {
        desc: 'Exterior has elegant, top-tier finishes',
        img: imgix('2023-04-1681773220487-59852.png?fm=jpg&auto=format'),
      },
    }),
  ],
});

const roomConditionPages = [
  kitchenCondition,
  bathroomCondition,
  livingRoomCondition,
  exteriorCondition,
];

export default roomConditionPages;
